/**
 * Get all parents or children of productCode.
 */

import { flatMap } from 'lodash';
import {
  ProductsByCode,
  EnrichedRelation,
  EnrichedParentRelation,
  ByProductCode,
  ProductLabelDatum,
} from '../input/products/types';

type ProductRelatives = Pick<ProductLabelDatum, 'parents' | 'children' | 'floating'>;
/**
 * Load the branch towards parents or children with optional maximum depth.
 * NOTE: This function does not include the original productCode itself.
 */
export function productLabelRelatives(
  productCode: string,
  relation: 'parents' | 'children',
  productsByCode: ByProductCode<ProductRelatives>,
  maxDepth?: number,
): string[] {
  return _productLabelRelatives(productCode, relation, productsByCode, 0, maxDepth);
}

export function getAllChildProducts(productCode: string, productsByCode: ByProductCode<ProductRelatives>) {
  return productLabelRelatives(productCode, 'children', productsByCode);
}

export function productIsWithinTree(productCode: string, parentProductCode: string, products: ProductsByCode) {
  if (productCode === parentProductCode) {
    return true;
  }
  const parents = productLabelRelatives(productCode, 'parents', products);
  return parents.indexOf(parentProductCode) !== -1;
}

export function _productLabelRelatives(
  productCode: string,
  relation: 'parents' | 'children',
  productsByCode: ByProductCode<ProductRelatives>,
  depth: number,
  maxDepth?: number,
): string[] {
  const datum = productsByCode[productCode];
  if (!datum || (maxDepth !== undefined && depth > maxDepth)) {
    return [];
  }
  const directRelations: Array<EnrichedRelation | EnrichedParentRelation> = datum[relation];
  const directRelationCodes = directRelations.map(d => d.code);
  return directRelationCodes.concat(
    flatMap(directRelationCodes, relationCode =>
      productLabelRelatives(relationCode, relation, productsByCode, depth + 1),
    ),
  );
}

export function getProductHierarchyBranch(
  productCode: string,
  productsByCode: ByProductCode<ProductRelatives>,
  includeFloaters?: boolean,
): string[] {
  const parentCodes = productLabelRelatives(productCode, 'parents', productsByCode);
  const childCodes = productLabelRelatives(productCode, 'children', productsByCode);
  const results = [...parentCodes, productCode, ...childCodes];

  if (includeFloaters) {
    for (const code of childCodes) {
      for (const relation of productsByCode[code].parents) {
        if (productsByCode[relation.code].floating) {
          results.push(relation.code);
        }
      }
    }
  }
  return results;
}
